import { CommitmentData, CurrencySettings, OverviewData } from '../types';
import api, { ApiInputs, ApiResponses } from 'lib/trpcClient';
import {
  mapCommitmentResponseToCommitmentData,
  mapInvestorBalanceResponseToCommitmentData,
} from './mapping';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDay } from 'further-ui/utils';
import { match, P } from 'ts-pattern';

export type UpdateCommitment = (
  input: Partial<{
    feeTemplate: OverviewData['feeTemplate'] | null;
    carriedInterestFeeTemplate:
      | OverviewData['carriedInterestFeeTemplate']
      | null;
    equalisationInterestFeeTemplate:
      | OverviewData['equalisationInterestFeeTemplate']
      | null;
    investorCurrency: CurrencySettings['investorCurrency'];
  }>,
) => void;

type UseCommitmentViewResult =
  | {
      isLoading: true;
      data: undefined;
      updateCommitment: UpdateCommitment;
      calculatingCurrencyConversions: boolean;
      investorName?: string;
      commitmentIds: Array<string>;
    }
  | {
      isLoading?: false;
      investorName: string;
      data: CommitmentData;
      commitmentIds: Array<string>;
      calculatingCurrencyConversions: boolean;
      updateCommitment: UpdateCommitment;
    };

export const useCommitmentView = ({
  commitmentId,
  vehicleId,
  investorId,
  date,
}: {
  commitmentId?: string | null;
  investorId: string;
  vehicleId: string;
  date: CalendarDay;
}): UseCommitmentViewResult => {
  const queryClient = useQueryClient();
  const utils = api.useUtils();
  const [multipleCommitmentData] =
    api.commitments.getInvestorBalance.useSuspenseQuery({
      investorId,
      vehicleId,
      date,
    });
  const commitmentIds = multipleCommitmentData.commitmentIds;

  const singleCommitmentId = match({ commitmentId, commitmentIds })
    .with({ commitmentId: P.not(P.nullish) }, (data) => data.commitmentId)
    .with(
      {
        commitmentId: P.nullish,
        commitmentIds: P.when((ids) => ids.length === 1),
      },
      (data) => data.commitmentIds[0],
    )
    .otherwise(() => '');

  const { data: singleCommitmentData, ...singleCommitmentQuery } =
    api.commitments.get.useQuery(
      {
        commitmentId: singleCommitmentId,
        vehicleId,
      },
      {
        enabled: !!singleCommitmentId,
      },
    );

  const singleCommitmentQueryKey = getQueryKey(api.commitments.get, {
    commitmentId: singleCommitmentId ?? undefined,
    vehicleId,
  });

  const multipleCommitmentQueryKey = getQueryKey(
    api.commitments.getInvestorBalance,
    {
      investorId,
      vehicleId,
      date,
    },
  );

  const listLpFeesQueryKey = getQueryKey(api.lpFee.list, {
    vehicleId,
  });

  const { mutateAsync: runUpdateCommitmentMutation } =
    api.commitments.update.useMutation(
      useHandleTrpcMutation<
        ApiResponses['commitments']['update'],
        ApiInputs['commitments']['update']
      >()({
        invalidations: [
          singleCommitmentQueryKey,
          multipleCommitmentQueryKey,
          listLpFeesQueryKey,
        ],
      }),
    );

  const {
    mutateAsync: runUpdateInvestorLocalCurrencyMutation,
    isPending: updateCurrencyMutationPending,
  } = api.lpInvestor.updateLocalCurrency.useMutation(
    useHandleTrpcMutation<
      ApiResponses['lpInvestor']['updateLocalCurrency'],
      ApiInputs['lpInvestor']['updateLocalCurrency']
    >()({
      invalidations: [
        ...commitmentIds.map((id) =>
          getQueryKey(api.commitments.get, {
            commitmentId: id,
            vehicleId,
          }),
        ),
        multipleCommitmentQueryKey,
      ],
    }),
  );

  const updateCommitment: UpdateCommitment = async (updateData) => {
    queryClient.cancelQueries({ queryKey: singleCommitmentQueryKey });
    commitmentIds.map((id) =>
      utils.commitments.get.setData(
        {
          commitmentId: id,
          vehicleId,
        },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            currencySettings: {
              ...oldData.currencySettings,
              investorCurrency:
                updateData.investorCurrency ??
                oldData.currencySettings.investorCurrency,
            },
            feeTemplate:
              id === singleCommitmentId && updateData.feeTemplate !== undefined
                ? updateData.feeTemplate ?? {}
                : oldData.feeTemplate,
            carriedInterestFeeTemplate:
              id === singleCommitmentId &&
              updateData.carriedInterestFeeTemplate !== undefined
                ? updateData.carriedInterestFeeTemplate ?? {}
                : oldData.carriedInterestFeeTemplate,
            equalisationInterestFeeTemplate:
              id === singleCommitmentId &&
              updateData.equalisationInterestFeeTemplate !== undefined
                ? updateData.equalisationInterestFeeTemplate ?? {}
                : oldData.equalisationInterestFeeTemplate,
          };
        },
      ),
    );
    queryClient.cancelQueries({ queryKey: multipleCommitmentQueryKey });
    utils.commitments.getInvestorBalance.setData(
      {
        investorId,
        vehicleId,
        date,
      },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          currencySettings: {
            ...oldData.currencySettings,
            investorCurrency:
              updateData.investorCurrency ??
              oldData.currencySettings.investorCurrency,
          },
        };
      },
    );
    if (updateData.investorCurrency) {
      runUpdateInvestorLocalCurrencyMutation({
        investorId,
        vehicleId,
        currency: updateData.investorCurrency,
      });
      return;
    } else if (singleCommitmentData && singleCommitmentId) {
      runUpdateCommitmentMutation({
        commitmentId: singleCommitmentId,
        vehicleId,
        commitmentDate: singleCommitmentData.commitmentDate,
        investorId: singleCommitmentData.investorId,
        loanCommitmentLcy: singleCommitmentData.loanCommitmentLcy,
        capitalCommitmentLcy: singleCommitmentData.capitalCommitmentLcy,
        totalCommitmentLcy: singleCommitmentData.totalCommitmentLcy,
        isBalancingEntity: singleCommitmentData.isBalancingEntity,
        carriedInterestFeeTemplateId:
          updateData.carriedInterestFeeTemplate !== undefined
            ? updateData.carriedInterestFeeTemplate?.id ?? null
            : singleCommitmentData.carriedInterestFeeTemplateId ?? undefined,
        equalisationInterestFeeTemplateId:
          updateData.equalisationInterestFeeTemplate !== undefined
            ? updateData.equalisationInterestFeeTemplate?.id ?? null
            : singleCommitmentData.equalisationInterestFeeTemplateId ??
              undefined,
        lpFeeTemplateId:
          updateData.feeTemplate !== undefined
            ? updateData.feeTemplate?.id ?? null
            : singleCommitmentData.lpFeeTemplateId ?? undefined,
        externalCommitmentId: singleCommitmentData.externalCommitmentId,
        investorCurrency:
          updateData.investorCurrency ??
          singleCommitmentData.currencySettings.investorCurrency,
      });
    }
  };

  const investorName =
    multipleCommitmentData?.investorName ??
    singleCommitmentData?.investorName ??
    '';

  const calculatingCurrencyConversions =
    updateCurrencyMutationPending || singleCommitmentQuery.isFetching;

  if (singleCommitmentId) {
    if (singleCommitmentQuery.isLoading || !singleCommitmentData) {
      return {
        isLoading: true,
        data: undefined,
        investorName,
        commitmentIds,
        calculatingCurrencyConversions,
        updateCommitment,
      };
    } else {
      const mappedData = mapCommitmentResponseToCommitmentData(
        singleCommitmentData,
        multipleCommitmentData.fundCloseHistory,
      );
      return {
        commitmentIds,
        investorName,
        calculatingCurrencyConversions,
        data: mappedData,
        updateCommitment,
      };
    }
  }

  if (!singleCommitmentId) {
    const mappedData = mapInvestorBalanceResponseToCommitmentData(
      multipleCommitmentData,
    );
    return {
      investorName,
      calculatingCurrencyConversions,
      data: mappedData,
      commitmentIds,
      updateCommitment,
    };
  }

  return {
    isLoading: true,
    calculatingCurrencyConversions: false,
    commitmentIds: [],
    investorName: undefined,
    data: undefined,
    updateCommitment,
  };
};
